import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/use-auth.ts';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { paths } from '@/config/paths';
import {
  Button,
  Input,
  Label,
  Logo,
  PageLoading,
} from 'greenback-shared/components';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate(paths.app.dashboard.getHref(), { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        const isInvalidCredentials =
          error.code === 'invalid_credentials' ||
          error.message === 'Invalid login credentials';
        toast.error(
          isInvalidCredentials
            ? 'Credenziali non valide. Riprova.'
            : 'Non riesco a farti entrare. Riprova.',
        );
      }
    } catch {
      toast.error('Non riesco a farti entrare. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking authentication
  if (authLoading || (user && !authLoading)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <PageLoading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="mx-auto w-full sm:max-w-md">
        <Logo height={44} className="mx-auto flex justify-center" />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
          Accedi al tuo account
        </h2>
      </div>

      <div className="mt-8 p-8 mx-auto w-full max-w-sm sm:max-w-md">
        <div className="bg-card py-8 px-4 shadow rounded-card sm:px-10 border border-border">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Inserisci la tua email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Inserisci la tua password"
              />
            </div>

            <div>
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'Accesso in corso...' : 'Accedi'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
